import { Calendar, Shield, TrendingUp } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import type {
  PreviousRecommendedProduct,
  PreviousRecommendations,
} from '@/hooks/api/usePreviousRecommendations';
import { formatDate } from '@/lib/formatters';
import { normalizeProductTypeLabel } from '@/lib/product-types';

interface PreviousRecommendationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recommendation: PreviousRecommendations | null | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}

function formatRate(rate: number | null, rateType: string | null): string {
  if (rate == null) return '—';
  if (rateType === 'participation') return `${rate}% participation`;
  if (rateType === 'fixed') return `${rate.toFixed(2)}% fixed`;
  return `${rate.toFixed(2)}% cap`;
}

export function PreviousProductCard({ product }: { product: PreviousRecommendedProduct }) {
  const strategies = product.strategyAllocations ?? [];
  const activeStrategies = [...strategies]
    .filter((strategy) => strategy.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);

  return (
    <Card
      className="relative z-10 flex flex-col border-border bg-surface-2 shadow-elevation-2"
      style={{ backgroundColor: 'hsl(var(--surface-2))' }}
    >
      <CardContent className="flex flex-1 flex-col p-5">
        <div className="flex min-h-[3.75rem] items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-2 text-sm font-semibold">
              {product.productName ?? 'Unknown Product'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {product.carrierName ?? 'Unknown Carrier'}
            </p>
          </div>
          {product.amBestRating && (
            <Badge variant="secondary" className="shrink-0">
              {product.amBestRating}
            </Badge>
          )}
        </div>

        {activeStrategies.length > 0 && (
          <div className="mt-3 space-y-2">
            {activeStrategies.map((strategy, index) => (
              <div
                key={`${strategy.accountName}-${strategy.accountSourceCode ?? index}`}
                className="rounded-md border border-primary/15 bg-primary/5 p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <TruncatedTooltip
                    text={strategy.accountName}
                    className="text-xs font-medium text-primary"
                  />
                  <span className="shrink-0 text-sm font-bold">
                    {formatRate(strategy.rate, strategy.rateType)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {strategy.percentage}% allocation
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto grid grid-cols-2 gap-x-4 gap-y-1.5 pt-3 text-xs">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Surrender</span>
            <span className="font-medium">
              {product.surrenderPeriodYears != null ? `${product.surrenderPeriodYears}yr` : '—'}
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Type</span>
            <span className="font-medium">{normalizeProductTypeLabel(product.productType)}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Strategies</span>
            <span className="font-medium">{activeStrategies.length}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Position</span>
            <span className="font-medium">{product.position ?? '—'}</span>
          </div>
        </div>

        <div className="mt-auto pt-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Match Score</span>
            <span className="font-medium">{product.recommendationScore ?? '—'}</span>
          </div>
          {product.recommendationScore != null && (
            <div
              className="relative mt-1 h-2 w-full overflow-hidden"
              style={{ backgroundColor: 'hsl(var(--muted) / 0.15)' }}
            >
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{
                  width: `${product.recommendationScore}%`,
                  backgroundColor:
                    product.recommendationScore >= 70
                      ? 'hsl(var(--success) / 0.5)'
                      : product.recommendationScore >= 50
                        ? 'hsl(var(--warning) / 0.5)'
                        : 'hsl(var(--destructive) / 0.5)',
                  clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 100%, 0 100%)',
                }}
              />
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {product.firelightCusip && (
            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
              <Shield className="mr-0.5 h-2.5 w-2.5" />
              Firelight Ready
            </Badge>
          )}
          {product.originalPosition != null && product.originalPosition !== product.position && (
            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
              <TrendingUp className="mr-0.5 h-2.5 w-2.5" />
              Reordered
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function PreviousRecommendationsModal({
  open,
  onOpenChange,
  recommendation,
  isLoading,
  error,
  onRetry,
}: PreviousRecommendationsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl border-border bg-gradient-card text-card-foreground shadow-elevation-3"
        style={{
          background:
            'linear-gradient(to bottom, hsl(var(--gradient-card-from)), hsl(var(--gradient-card-to)))',
          borderColor: 'hsl(var(--border))',
          boxShadow: 'var(--shadow-3)',
        }}
      >
        <DialogHeader>
          <DialogTitle>Previously Recommended Products</DialogTitle>
          <DialogDescription>
            Products that were last forwarded to the agent platform for this opportunity.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-64 animate-pulse rounded-lg bg-muted/40" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
            <p className="text-sm text-destructive">Failed to load previous recommendations.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : !recommendation || recommendation.products.length === 0 ? (
          <div className="rounded-lg border border-border bg-muted/20 p-6 text-sm text-muted-foreground">
            No previously recommended products were found for this opportunity.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              Forwarded on {formatDate(recommendation.forwardedAt)}
              {recommendation.forwardedBy ? ` by ${recommendation.forwardedBy}` : ''}
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {recommendation.products.map((product) => (
                <PreviousProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
